// src/components/CreateChapter.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db, storage } from "../firebase";
import {
    collection,
    addDoc,
    doc,
    updateDoc,
    arrayUnion,
    getDoc
} from "firebase/firestore";
import {
    ref,
    uploadBytes,
    getDownloadURL
} from "firebase/storage";

const CreateChapter = () => {
    const [title, setTitle] = useState("");
    const [type, setType] = useState("text");
    const [textContent, setTextContent] = useState("");
    const [urlContent, setUrlContent] = useState("");
    const [file, setFile] = useState(null);
    const [error, setError] = useState("");
    const [uploading, setUploading] = useState(false);

    const navigate = useNavigate();
    const user = auth.currentUser;

    const WORD_LIMIT = 200;

    const countWords = (str) =>
        str.trim().length === 0 ? 0 : str.trim().split(/\s+/).length;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        // Word limit validation
        if (type === "text" && countWords(textContent) > WORD_LIMIT) {
            setError(`Text pages are limited to ${WORD_LIMIT} words.`);
            return;
        }

        setUploading(true);

        try {
            let storagePath = "";
            let contentValue = "";

            if (type === "text") {
                if (!textContent.trim()) {
                    setError("Text content cannot be empty.");
                    setUploading(false);
                    return;
                }
                contentValue = textContent;
            } else if (type === "url") {
                contentValue = urlContent;
            } else if (file) {
                const fileRef = ref(
                    storage,
                    `books/${user.uid}/content/${Date.now()}_${file.name}`
                );
                await uploadBytes(fileRef, file);
                storagePath = await getDownloadURL(fileRef);
            } else {
                setError("File is required for this content type.");
                setUploading(false);
                return;
            }

            const contentDoc = await addDoc(collection(db, "content"), {
                Title: title,
                Type: type,
                StoragePath: storagePath || contentValue
            });

            const bookRef = doc(db, "books", user.uid);
            await updateDoc(bookRef, {
                contents: arrayUnion(contentDoc.id)
            });

            const bookSnap = await getDoc(bookRef);
            const bookData = bookSnap.data();

            if (!bookData.published && bookData.contents.length === 0) {
                await updateDoc(bookRef, { published: true });
            }

            navigate("/manage-book");
        } catch (err) {
            setError("Error creating page: " + err.message);
        } finally {
            setUploading(false);
        }
    };

    const renderInput = () => {
        switch (type) {
            case "text":
                return (
                    <div>
                        <textarea
                            className="form-control"
                            rows={6}
                            value={textContent}
                            onChange={(e) => {
                                const words = countWords(e.target.value);
                                if (words <= WORD_LIMIT) {
                                    setTextContent(e.target.value);
                                }
                            }}
                            required
                        />
                        <p
                            className={`text-end small mt-1 ${countWords(textContent) >= WORD_LIMIT ? "text-danger" : "text-muted"
                                }`}
                        >
                            {countWords(textContent)} / {WORD_LIMIT} words
                        </p>
                    </div>
                );

            case "url":
                return (
                    <input
                        type="url"
                        className="form-control"
                        value={urlContent}
                        onChange={(e) => setUrlContent(e.target.value)}
                        required
                    />
                );

            default:
                return (
                    <input
                        type="file"
                        className="form-control"
                        accept={
                            type === "audio"
                                ? "audio/*"
                                : type === "video"
                                    ? "video/*"
                                    : type === "image"
                                        ? "image/*"
                                        : type === "pdf"
                                            ? "application/pdf"
                                            : "*"
                        }
                        onChange={(e) => setFile(e.target.files[0])}
                        required
                    />
                );
        }
    };

    return (
        <div className="container mt-5" style={{ maxWidth: 600 }}>
            <h2 className="mb-4 text-center">Create New Page</h2>

            {error && <div className="alert alert-danger">{error}</div>}

            <form onSubmit={handleSubmit}>
                <div className="mb-3">
                    <label>Page Title</label>
                    <input
                        type="text"
                        className="form-control"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        required
                    />
                </div>

                <div className="mb-3">
                    <label>Page Type</label>
                    <select
                        className="form-select"
                        value={type}
                        onChange={(e) => {
                            setType(e.target.value);
                            setError("");
                        }}
                    >
                        <option value="text">Text</option>
                        <option value="audio">Audio</option>
                        <option value="video">Video</option>
                        <option value="image">Image</option>
                        <option value="pdf">PDF</option>
                        <option value="url">URL</option>
                    </select>
                </div>

                <div className="mb-3">
                    <label>Content</label>
                    {renderInput()}
                </div>

                <button
                    type="submit"
                    className="btn btn-success w-100"
                    disabled={uploading}
                >
                    {uploading ? "Uploading..." : "Save Page"}
                </button>
            </form>
        </div>
    );
};

export default CreateChapter;
